import { Loader2, Trash2, TrendingUp, MousePointerClick, Eye, Users, Star, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { TrafficStat, DANGER_BUTTON_CLASS } from "./DashboardStats";
import { ConfirmDangerDialog } from "./ConfirmDangerDialog";
import { CHANNELS, CHANNEL_COLORS, CHANNEL_SHORT, PANEL_CLASS } from "./dashboardUtils";

// Stable style/config objects (module scope) so recharts children are not re-created on every render.
const CHART_MARGIN = { top: 8, right: 16, bottom: 4, left: -12 };
const AXIS_TICK = { fill: "#647384", fontSize: 11 };
const CLICK_DOT = { r: 3, fill: "#F7B838" };
const ACTIVE_DOT = { r: 5 };
const UNIQUE_DOT = { r: 2.5, fill: "#1F9188" };
const SWATCH_CLICKS = { background: "#F7B838" };
const SWATCH_UNIQUE = { background: "#1F9188" };

const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid rgba(17,42,67,0.12)", fontSize: 13 };

const BestPerformerCallout = ({ row }) => (
  <div
    data-testid="best-performer"
    className="mx-5 mt-5 flex items-center gap-3 rounded-xl border border-[rgba(247,184,56,0.45)] bg-[rgba(247,184,56,0.10)] px-4 py-3"
  >
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7B838] text-[#071524]">
      <Star size={18} className="fill-[#071524]" />
    </span>
    <div className="min-w-0 text-sm">
      <span className="font-semibold text-[#122131]">Top performer: {row.name}</span>
      <span className="text-[#647384]">
        {" "}
        — {row.conv.toFixed(1)}% conversion ({row.regs} sign-up
        {row.regs === 1 ? "" : "s"} from {row.unique} unique visitor
        {row.unique === 1 ? "" : "s"})
      </span>
    </div>
  </div>
);

const DailyTrendChart = ({ data }) => (
  <div className="px-5 pt-5" data-testid="traffic-trend">
    <div className="flex items-center gap-2 mb-2">
      <BarChart3 size={16} className="text-[#126F6A]" />
      <h3 className="text-sm font-semibold text-[#122131]">Daily clicks trend</h3>
    </div>
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,42,67,0.08)" vertical={false} />
        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={16} />
        <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} width={36} />
        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n) => [v, n === "unique" ? "Unique" : "Clicks"]} />
        <Line type="monotone" dataKey="clicks" stroke="#F7B838" strokeWidth={2.5} dot={CLICK_DOT} activeDot={ACTIVE_DOT} />
        <Line type="monotone" dataKey="unique" stroke="#1F9188" strokeWidth={2} dot={UNIQUE_DOT} strokeDasharray="4 3" />
      </LineChart>
    </ResponsiveContainer>
    <div className="flex items-center gap-4 mt-1 text-xs text-[#647384]">
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-4 rounded-full" style={SWATCH_CLICKS} /> Clicks</span>
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-4 rounded-full" style={SWATCH_UNIQUE} /> Unique</span>
    </div>
  </div>
);

const ChannelBadges = ({ channels }) => {
  const active = CHANNELS.filter((c) => (channels[c] || 0) > 0);
  if (active.length === 0) return <span className="text-[#647384]">{"\u2014"}</span>;
  return active.map((c) => (
    <span
      key={c}
      title={`${c}: ${channels[c]} click${channels[c] === 1 ? "" : "s"}`}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: `${CHANNEL_COLORS[c]}1A`, color: CHANNEL_COLORS[c] }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: CHANNEL_COLORS[c] }} />
      {CHANNEL_SHORT[c]} {channels[c]}
    </span>
  ));
};

const TrafficRow = ({ row, isBest }) => {
  const isDirect = row.code === "DIRECT";
  return (
    <tr
      data-testid={`traffic-row-${row.code}`}
      className="border-b border-[rgba(17,42,67,0.06)] hover:bg-[#FBF8F0]"
      style={isBest ? { background: "rgba(247,184,56,0.06)" } : undefined}
    >
      <td className="px-5 py-3 whitespace-nowrap">
        {isBest && <Star size={14} className="inline-block mr-1.5 -mt-0.5 text-[#F7B838] fill-[#F7B838]" />}
        <span className="font-medium text-[#122131]">{row.name}</span>
        {!isDirect && <span className="ml-2 text-xs font-semibold text-[#126F6A]">{row.code}</span>}
        {isBest && (
          <span className="ml-2 rounded-full bg-[#F7B838] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#071524]">
            Top
          </span>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <ChannelBadges channels={row.channels} />
        </div>
      </td>
      <td className="px-5 py-3 text-right font-semibold text-[#122131]">{row.clicks}</td>
      <td className="px-5 py-3 text-right text-[#122131]">{row.unique}</td>
      <td className="px-5 py-3 text-right text-[#122131]">{row.regs}</td>
      <td className="px-5 py-3 text-right">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{
            background: row.conv > 0 ? "rgba(18,111,106,0.10)" : "rgba(17,42,67,0.06)",
            color: row.conv > 0 ? "#126F6A" : "#647384",
          }}
        >
          {row.unique > 0 ? `${row.conv.toFixed(1)}%` : "\u2014"}
        </span>
      </td>
    </tr>
  );
};

export const TrafficPanel = ({
  rows,
  totals,
  bestPerformer,
  dailyTrend,
  loading,
  clearing,
  onReset,
}) => (
  <div className={PANEL_CLASS} data-testid="link-traffic-table">
    <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[rgba(17,42,67,0.08)]">
      <div className="flex items-center gap-2">
        <TrendingUp size={20} className="text-[#126F6A]" />
        <div>
          <h2 className="text-lg font-bold text-[#122131]">Link Traffic &amp; Conversion</h2>
          <p className="text-sm text-[#647384]">
            Clicks and unique visitors per link, with sign-up conversion. Tracking runs live from now.
          </p>
        </div>
      </div>
      <ConfirmDangerDialog
        dialogTestId="reset-traffic-dialog"
        cancelTestId="reset-traffic-cancel"
        confirmTestId="reset-traffic-confirm"
        title="Reset all traffic data?"
        description="This permanently clears all recorded clicks and unique-visitor data for this event. Registrations are not affected. This cannot be undone."
        confirmLabel="Reset traffic"
        onConfirm={onReset}
        trigger={
          <button
            data-testid="dashboard-reset-traffic"
            disabled={loading || clearing || totals.clicks === 0}
            className={`shrink-0 ${DANGER_BUTTON_CLASS}`}
          >
            {clearing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Reset traffic
          </button>
        }
      />
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[rgba(17,42,67,0.06)] border-b border-[rgba(17,42,67,0.08)]">
      <TrafficStat icon={MousePointerClick} label="Total clicks" value={totals.clicks} />
      <TrafficStat icon={Eye} label="Unique visitors" value={totals.unique} />
      <TrafficStat icon={Users} label="Sign-ups" value={totals.regs} />
      <TrafficStat icon={TrendingUp} label="Overall conversion" value={`${totals.conv.toFixed(1)}%`} />
    </div>

    {bestPerformer && <BestPerformerCallout row={bestPerformer} />}
    {dailyTrend.length > 1 && <DailyTrendChart data={dailyTrend} />}

    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-10 text-center text-[#647384]">
          No link traffic recorded yet. Clicks will appear here as people open your links.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="traffic-table">
          <thead>
            <tr className="text-left text-[#647384] border-b border-[rgba(17,42,67,0.08)]">
              <th className="px-5 py-3 font-medium">Link / Adviser</th>
              <th className="px-5 py-3 font-medium">Sources</th>
              <th className="px-5 py-3 font-medium text-right">Clicks</th>
              <th className="px-5 py-3 font-medium text-right">Unique</th>
              <th className="px-5 py-3 font-medium text-right">Sign-ups</th>
              <th className="px-5 py-3 font-medium text-right">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <TrafficRow key={r.code} row={r} isBest={Boolean(bestPerformer && r.code === bestPerformer.code)} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);
